import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { projectsApi } from '../../../api/projects';
import { ApiError } from '../../../api/client';
import { IconActionButton } from '../../../components/IconActionButton';
import { Trash2 } from 'lucide-react';
import { SectionHeader } from '../../../components/SectionHeader';
import { DataTable } from '../../../components/DataTable';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { Project } from '../../../types/projects';
import { CommentThread } from '../../../components/CommentThread';
import { UserPicker } from '../../../components/UserPicker';

export function ProjectTeamTab({ project }: { project: Project }) {
  const canManageTeam = usePermission('project_team.manage');
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const teamQuery = useQuery({ queryKey: ['project-team', project.id], queryFn: () => projectsApi.listTeam(project.id) });
  const rolesQuery = useQuery({ queryKey: ['project-roles'], queryFn: () => projectsApi.listRoles() });

  const [teamUserId, setTeamUserId] = useState('');
  const [teamRoleId, setTeamRoleId] = useState('');
  const addTeamMutation = useMutation({
    mutationFn: () => projectsApi.addTeamMember(project.id, { userId: Number(teamUserId), projectRoleId: teamRoleId ? Number(teamRoleId) : null }),
    onSuccess: () => {
      showSuccess('Team member added.');
      queryClient.invalidateQueries({ queryKey: ['project-team', project.id] });
      setTeamUserId(''); setTeamRoleId('');
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to add team member.'),
  });
  const removeTeamMutation = useMutation({
    mutationFn: (userId: number) => projectsApi.removeTeamMember(project.id, userId),
    onSuccess: () => { showSuccess('Team member removed.'); queryClient.invalidateQueries({ queryKey: ['project-team', project.id] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to remove team member.'),
  });

  return (
    <>
    <div className="panel">
      <SectionHeader title="Project Team" />
      <div className="panel-body">
      <p className="muted">Team membership determines project-scoped visibility via the existing RBACEngine.</p>
      {teamQuery.data && (
        <DataTable
          rows={teamQuery.data.data}
          rowKey={(t) => t.id}
          emptyMessage="No team members assigned."
          columns={[
            { header: 'Name', render: (t) => t.full_name },
            { header: 'Role', render: (t) => t.role_name ?? '-' },
            { header: '', render: (t) => canManageTeam ? <IconActionButton icon={Trash2} label="Remove" danger onClick={() => removeTeamMutation.mutate(t.user_id)} /> : null },
          ]}
        />
      )}
      {canManageTeam && (
        <div className="inline-form">
          <label>User<UserPicker value={teamUserId} onChange={(e) => setTeamUserId(e.target.value)} /></label>
          <label>
            Project Role
            <select value={teamRoleId} onChange={(e) => setTeamRoleId(e.target.value)}>
              <option value="">- none -</option>
              {rolesQuery.data?.data.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
          <button disabled={!teamUserId || addTeamMutation.isPending} onClick={() => addTeamMutation.mutate()}>Add</button>
        </div>
      )}
      </div>
    </div>

    <CommentThread projectId={project.id} entityType="team" />
    </>
  );
}
